export const isBrowserRuntime = () =>
  typeof window !== 'undefined' && !!window.location && !!window.location.origin;

/**
 * Base URL for same-origin `/api/*` calls.
 *
 * Browser: current page origin.
 * Node SSR / scripts: loopback so requests stay on the local Node process instead
 * of REACT_APP_MARKETPLACE_ROOT_URL (the public CloudFront/WAF edge, which now
 * challenges non-browser traffic and would return a 403 HTML page to our own SSR
 * self-fetches).
 *
 * IMPORTANT: app code in `src/` is bundled by webpack, whose DefinePlugin replaces
 * the entire `process.env` object with ONLY the whitelisted vars (NODE_ENV +
 * REACT_APP_*). That means `process.env.PORT` is compiled to `undefined` inside the
 * SSR bundle even though the running Node process has PORT set.
 *
 * Prefer order for SSR:
 * 1) REACT_APP_INTERNAL_API_BASE_URL (survives DefinePlugin; set in production.env /
 *    staging.env to http://127.0.0.1:PORT)
 * 2) Runtime global published by server/index.js (belt-and-suspenders)
 * 3) process.env.PORT (raw-node / non-bundled scripts only)
 * 4) REACT_APP_MARKETPLACE_ROOT_URL (last resort — hairpins through the public edge)
 */
export const SSR_API_ORIGIN_GLOBAL = '__PROMOTIX_SSR_API_ORIGIN__';

const trimOrigin = value => (value ? String(value).replace(/\/+$/, '') : '');

export const sameOriginApiBase = () => {
  if (isBrowserRuntime()) {
    return window.location.origin;
  }

  // 1) Build-time REACT_APP_* — the only env form webpack reliably inlines into the
  // SSR bundle. Set this in production.env / staging.env before `yarn build`.
  const internal =
    typeof process !== 'undefined' ? process.env.REACT_APP_INTERNAL_API_BASE_URL : null;
  if (internal) {
    return trimOrigin(internal);
  }

  // 2) Loopback origin injected by the Node server at boot.
  const injectedFromGlobalThis =
    typeof globalThis !== 'undefined' ? globalThis[SSR_API_ORIGIN_GLOBAL] : null;
  const injectedFromGlobal =
    typeof global !== 'undefined' ? global[SSR_API_ORIGIN_GLOBAL] : null;
  const injected = injectedFromGlobalThis || injectedFromGlobal;
  if (injected) {
    return trimOrigin(injected);
  }

  const devPort =
    typeof process !== 'undefined' ? process.env.REACT_APP_DEV_API_SERVER_PORT : null;
  if (process.env.NODE_ENV === 'development' && devPort) {
    return `http://localhost:${devPort}`;
  }

  // 3) Raw-node contexts (CLI scripts run via babel, not webpack) still see PORT.
  const port = typeof process !== 'undefined' ? process.env.PORT : null;
  if (port) {
    return `http://127.0.0.1:${port}`;
  }

  const root =
    typeof process !== 'undefined' ? process.env.REACT_APP_MARKETPLACE_ROOT_URL || '' : '';
  return trimOrigin(root);
};
